import { put, select } from 'redux-saga/effects';

import { GeneratorResult, SagaResult, takeIf } from '@pressreader/appstore';
import { IContextMenuPosition } from '@pressreader/popups-types';
import { PopupContainerDynamicSizePositionCalculationStrategy } from '@pressreader/ui-popups';
import { newGuid } from '@pressreader/utils';

import { PopupType } from '../types';

import { closeContextMenuRequestAction, contextMenuCloseDone, dialogCloseDone, openContextMenuAction, openDialogAction } from './actions';
import { selectPopupById } from './selectors';

function* openContextMenu<IContextMenuContext>(params: {
    parentMenuId?: string;
    name: string;
    context: IContextMenuContext;
    position: IContextMenuPosition;
    positionCalculationStrategy?: PopupContainerDynamicSizePositionCalculationStrategy;
}): SagaResult<string> {
    const menuId = newGuid();
    yield put(
        openContextMenuAction({
            menu: {
                type: PopupType.ContextMenu,
                id: menuId,
                parentId: params.parentMenuId,
                name: params.name,
                context: params.context,
                position: params.position,
                positionCalculationStrategy: params.positionCalculationStrategy,
            },
        }),
    );

    return menuId;
}

function* closeContextMenu(contextMenuId: string, params: { back?: boolean }): SagaResult<void> {
    const popup: ReturnType<typeof selectPopupById> = yield select(selectPopupById, contextMenuId);

    if (popup?.type !== PopupType.ContextMenu) {
        return;
    }

    yield put(contextMenuCloseDone({ id: contextMenuId, back: params.back }));

    if (!params.back) {
        if (popup.parentId) {
            yield put(closeContextMenuRequestAction({ id: popup.parentId, cancelled: true }));
        }
    }
}

function* takeCloseContextMenuRequest(contextMenuId: string): GeneratorResult<ReturnType<typeof closeContextMenuRequestAction>> {
    return yield takeIf(closeContextMenuRequestAction, action => action.payload.id === contextMenuId);
}

function* openDialog<TDialogContext>(name: string, context: TDialogContext, supportNativeScroll = true): SagaResult<string> {
    const dialogId = newGuid();

    yield put(openDialogAction({ dialog: { type: PopupType.Dialog, id: dialogId, name, context, supportNativeScroll } }));

    return dialogId;
}

function* closeDialog(dialogId: string): SagaResult<void> {
    yield put(dialogCloseDone({ id: dialogId }));
}

export { closeContextMenu, closeDialog, openContextMenu, openDialog, takeCloseContextMenuRequest };
